import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CreditCard, ShieldCheck, Tag, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { gbp } from "@/lib/constants";

export default function Checkout() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [promo, setPromo] = useState("");
  const [discount, setDiscount] = useState(0);
  const [paying, setPaying] = useState(false);

  useEffect(() => { api.get(`/bookings/${bookingId}`).then(({ data }) => setBooking(data)).catch(() => toast.error("Booking not found")); }, [bookingId]);

  if (!booking) return <div className="flex h-96 items-center justify-center"><Loader2 className="animate-spin text-brand" size={32} /></div>;

  const applyPromo = async () => {
    try {
      const { data } = await api.post("/promo/validate", { code: promo, fare: booking.total, service_type: booking.service_type });
      setDiscount(data.discount);
      toast.success(`Promo applied — you saved ${gbp(data.discount)}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid promo code"); setDiscount(0); }
  };

  const total = Math.max(0, booking.total - discount);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", { booking_id: booking.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch { toast.error("Could not start payment"); setPaying(false); }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="font-heading text-2xl font-bold text-navy">Checkout</h1>
      <p className="mt-1 text-sm text-slate-500">Booking {booking.booking_ref}</p>

      <div className="mt-6 grid gap-6 md:grid-cols-5">
        <div className="md:col-span-3 rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-heading text-lg font-semibold text-navy">Booking Summary</h2>
          <div className="mt-4 grid gap-2.5 text-sm">
            <Row label="Pickup" value={booking.pickup?.name} />
            {booking.dropoff && <Row label="Destination" value={booking.dropoff.name} />}
            <Row label="Date" value={booking.date} />
            <Row label="Time" value={booking.time} />
            <Row label="Vehicle" value={booking.vehicle_label} />
            <Row label="Operator" value={booking.operator_name} />
            <Row label="Passengers" value={booking.passengers?.adults + (booking.passengers?.children || 0)} />
            {booking.extras?.length > 0 && <Row label="Extras" value={booking.extras.length + " added"} />}
          </div>

          <div className="mt-5">
            <label className="mb-1 block text-sm font-medium text-navy">Promo code</label>
            <div className="flex gap-2">
              <div className="flex flex-1 items-center gap-2 rounded-lg border border-slate-200 px-3">
                <Tag size={15} className="text-brand" />
                <Input value={promo} onChange={(e) => setPromo(e.target.value)} data-testid="promo-input" placeholder="e.g. WELCOME10" className="border-0 px-0 focus-visible:ring-0" />
              </div>
              <Button variant="outline" onClick={applyPromo} data-testid="apply-promo">Apply</Button>
            </div>
            {discount > 0 && <p className="mt-2 flex items-center gap-1 text-sm text-emerald-600"><Check size={14} /> Discount applied</p>}
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Fare</span><span>{gbp(booking.fare)}</span></div>
              {booking.extras_total > 0 && <div className="flex justify-between"><span className="text-slate-500">Extras</span><span>{gbp(booking.extras_total)}</span></div>}
              {discount > 0 && <div className="flex justify-between text-emerald-600"><span>Discount</span><span>−{gbp(discount)}</span></div>}
            </div>
            <div className="my-4 h-px bg-slate-200" />
            <div className="flex items-center justify-between"><span className="font-heading font-semibold text-navy">Total</span><span className="font-heading text-2xl font-bold text-navy" data-testid="checkout-total">{gbp(total)}</span></div>
            <Button className="mt-5 w-full gap-2 bg-brand hover:bg-brand/90" onClick={pay} disabled={paying} data-testid="pay-confirm-btn">
              {paying ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />} Pay & Confirm Booking
            </Button>
            <p className="mt-3 flex items-center justify-center gap-2 text-xs text-slate-400"><ShieldCheck size={13} className="text-emerald-500" /> Secure payment via Stripe</p>
            <p className="mt-2 text-center text-xs text-slate-400">Card, Apple Pay & Google Pay accepted</p>
          </div>
        </div>
      </div>
    </div>
  );
}
function Row({ label, value }) {
  return <div className="flex items-center justify-between border-b border-slate-100 pb-2"><span className="text-slate-500">{label}</span><span className="font-medium text-navy">{value}</span></div>;
}
